import { Link } from "react-router-dom";
import { ArrowSquareOut, MapPin, Globe } from "@phosphor-icons/react";
import { SISTER_PLATFORMS } from "../lib/sisterPlatforms";

/**
 * Global site footer. Renders the "iAAM Network" sister-platforms block, secondary
 * nav, and the legal strip. Mounted from AppShell.
 */
export default function Footer() {
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  return (
    <footer className="border-t border-[#27272A] mt-16 bg-[#0A0A0C]" data-testid="site-footer">
      {/* Sister platforms band */}
      <section className="border-b border-[#27272A]" data-testid="sister-platforms-band">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
          <div className="flex items-center gap-2 mb-6">
            <Globe size={12} weight="bold" style={{ color: "#E60012" }} />
            <span className="label-mono" style={{ color: "#E60012" }}>THE IAAM NETWORK</span>
          </div>
          <h3 className="font-display text-white mb-8 text-2xl" style={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
            Sister platforms
          </h3>
          <div className="grid md:grid-cols-2 gap-5">
            {SISTER_PLATFORMS.map((p) => (
              <a
                key={p.key}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="card card-hover p-5 flex items-start gap-4 group"
                data-testid={`sister-card-${p.key}`}
                style={{ borderLeft: `2px solid ${p.accent}` }}
              >
                <div className="shrink-0 w-14 h-14 bg-[#0A0A0C] border border-[#27272A] overflow-hidden flex items-center justify-center">
                  <img
                    src={`${backend}${p.logo}`}
                    alt={`${p.name} logo`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.target.style.display = "none"; }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-white font-medium">{p.name}</span>
                    {p.badge && (
                      <span className="label-mono px-1.5 py-0.5 border" style={{ color: p.accent, borderColor: p.accent }}>
                        {p.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-300 line-clamp-3 leading-snug mb-3">{p.description}</p>
                  <span className="label-mono group-hover:text-white flex items-center gap-1" style={{ color: p.accent }}>
                    VISIT {new URL(p.url).host.toUpperCase()} <ArrowSquareOut size={9} weight="bold" />
                  </span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Secondary nav + legal */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid md:grid-cols-12 gap-8 text-sm">
        <div className="md:col-span-4">
          <div className="font-display text-white text-lg mb-2" style={{ fontWeight: 800, letterSpacing: "-0.015em" }}>iAAM</div>
          <p className="text-zinc-400 leading-relaxed">
            Integration of Aerospace and Automotive Mobility — the smart future-mobility intelligence community.
          </p>
        </div>
        <div className="md:col-span-2">
          <div className="label-mono mb-3">PLATFORM</div>
          <ul className="space-y-1.5 text-zinc-400">
            <li><Link to="/feed" className="hover:text-white">Feed</Link></li>
            <li><Link to="/agents" className="hover:text-white">AI Agents</Link></li>
            <li><Link to="/economy" className="hover:text-white">Economy</Link></li>
            <li><Link to="/live" className="hover:text-white">Live</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <div className="label-mono mb-3">CONTENT</div>
          <ul className="space-y-1.5 text-zinc-400">
            <li><Link to="/news" className="hover:text-white">News</Link></li>
            <li><Link to="/live-tv" className="hover:text-white">Live TV</Link></li>
            <li><Link to="/courses" className="hover:text-white">Courses</Link></li>
            <li><Link to="/orgs" className="hover:text-white">Organizations</Link></li>
          </ul>
        </div>
        <div className="md:col-span-4 md:text-right">
          <div className="label-mono mb-3 md:text-right">CONTACT</div>
          <p className="text-zinc-400 flex items-center md:justify-end gap-1.5">
            <MapPin size={11} weight="regular" /> iAAM HQ · Smart Mobility Network
          </p>
          <a href="mailto:hello@iaam.one" className="text-zinc-300 hover:text-white">hello@iaam.one</a>
        </div>
      </section>

      <section className="border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-wrap items-center justify-between gap-3 label-mono">
          <span>© {new Date().getFullYear()} iAAM. ALL RIGHTS RESERVED.</span>
          <span className="flex items-center gap-3">
            <a href="https://airdropstravels.com" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="footer-link-travels">AIRDROPS TRAVELS ↗</a>
            <a href="https://airdropspunch.com" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="footer-link-punch">AIRDROPS PUNCH ↗</a>
          </span>
        </div>
      </section>
    </footer>
  );
}
